// PERMANENT — do not remove with the rest of the migration. These back the `/discounts-classic`
// route, which is a supported fallback rather than a leftover. `uploadDiscountImage` is used by
// BOTH paths (cds stores the resulting URL in `displayImageUrl`), so it is not legacy-only.
import {
    fetchDiscountRules as fetchDiscountRulesReq,
    updateDiscountRule as updateDiscountRuleReq,
    createDiscountRule as createDiscountRuleReq,
    uploadDiscountImage as uploadDiscountImageHttpRequest,
    notifyDiscountChange as notifyDiscountChangeReq,
} from '../api.js';
import { v1 as uuidv1, v4 as uuidv4 } from 'uuid';
import { useDiscountService, sourceLabel } from '../services/discountSource.js';
import {
    createDiscount,
    deleteDiscount,
    listDiscounts,
    patchDiscountMetadata,
} from '../services/discountApi.js';
import {
    buildLocationTree,
    cdsListToLegacyRules,
    collectUnresolved,
    isPatchOnlyChange,
    legacyRuleToCdsPayloads,
} from '../services/discountMapper.js';
import { createUserGroup, deleteUserGroup, isStillInUse } from '../services/newCoreApi.js';
import { fetchLocations } from './cabinets.js';
import { fetchMerchant } from './merchants.js';
import { fetchRefMaps, resetRefMaps } from './refMaps.js';
export const REQUEST_DISCOUNT_RULES = 'REQUEST_DISCOUNT_RULES';
export const RECEIVE_DISCOUNT_RULES = 'RECEIVE_DISCOUNT_RULES';
export const RECEIVE_DISCOUNT_RULES_ERROR = 'RECEIVE_DISCOUNT_RULES_ERROR';

export const REQUEST_CREATE_DISCOUNT_RULE = 'REQUEST_CREATE_DISCOUNT_RULE';
export const RECEIVE_CREATE_DISCOUNT_RULE = 'RECEIVE_CREATE_DISCOUNT_RULE';
export const RECEIVE_CREATE_DISCOUNT_RULE_ERROR = 'RECEIVE_CREATE_DISCOUNT_RULE_ERROR';

export const REQUEST_DELETE_DISCOUNT_RULE = 'REQUEST_DELETE_DISCOUNT_RULE';
export const RECEIVE_DELETE_DISCOUNT_RULE = 'RECEIVE_DELETE_DISCOUNT_RULE';
export const RECEIVE_DELETE_DISCOUNT_RULE_ERROR = 'RECEIVE_DELETE_DISCOUNT_RULE_ERROR';

export const REQUEST_UPDATE_DISCOUNT_RULE = 'REQUEST_UPDATE_DISCOUNT_RULE';
export const RECEIVE_UPDATE_DISCOUNT_RULE = 'RECEIVE_UPDATE_DISCOUNT_RULE';
export const RECEIVE_UPDATE_DISCOUNT_RULE_ERROR = 'RECEIVE_UPDATE_DISCOUNT_RULE_ERROR';
export const RESET_DISCOUNT_RULES = 'RESET_DISCOUNT_RULES';

export const UPLOAD_BUNDLE_IMAGE_ERROR = 'UPLOAD_BUNDLE_IMAGE_ERROR';
export const UPLOAD_BUNDLE_IMAGE_SUCCESS = 'UPLOAD_BUNDLE_IMAGE_SUCCESS';
export const REQUEST_UPLOAD_BUNDLE_IMAGE = 'REQUEST_UPLOAD_BUNDLE_IMAGE';

export const RESET_BUNDLE_IMAGE = 'RESET_BUNDLE_IMAGE';

export const UPLOAD_SCHEDULED_IMAGE_ERROR = 'UPLOAD_SCHEDULED_IMAGE_ERROR';
export const UPLOAD_SCHEDULED_IMAGE_SUCCESS = 'UPLOAD_SCHEDULED_IMAGE_SUCCESS';
export const REQUEST_UPLOAD_SCHEDULED_IMAGE = 'REQUEST_UPLOAD_SCHEDULED_IMAGE';

export const RESET_SCHEDULED_IMAGE = 'RESET_SCHEDULED_IMAGE';

export const UPLOAD_EXPIRY_IMAGE_ERROR = 'UPLOAD_EXPIRY_IMAGE_ERROR';
export const UPLOAD_EXPIRY_IMAGE_SUCCESS = 'UPLOAD_EXPIRY_IMAGE_SUCCESS';
export const REQUEST_UPLOAD_EXPIRY_IMAGE = 'REQUEST_UPLOAD_EXPIRY_IMAGE';

export const RESET_EXPIRY_IMAGE = 'RESET_EXPIRY_IMAGE';

/**
 * Request discount rules Redux action creator
 * @param {'cds'|'legacy'} source backend this request is going to — see `discountSource.js`
 */
function requestDiscountRules(source) {
    return {
        type: REQUEST_DISCOUNT_RULES,
        source,
    };
}

/**
 * Receive discount rules Redux action creator
 * @param {array} discountRules array of discount rules received from root api
 * @param {'cds'|'legacy'} source backend that produced them
 */
function receiveDiscountRules(discountRules, source) {
    return {
        type: RECEIVE_DISCOUNT_RULES,
        discountRules,
        source,
    };
}

/**
 * Receive discount rules error Redux action creator
 * @param {Error} error api request error
 * @param {'cds'|'legacy'} source backend that failed
 */
function receiveDiscountRulesError(error, source) {
    return {
        type: RECEIVE_DISCOUNT_RULES_ERROR,
        error: error,
        source,
    };
}

/**
 * Thunk action for fetching discount rules
 */
export function fetchDiscountRules() {
    return (dispatch, getState) => {
        // Resolved ONCE, from the store's pathname, and stamped onto every action this thunk
        // dispatches. The page's fetch gate asks the same question of the same state, so "which
        // backend does the page want" and "which backend did this data come from" cannot drift.
        const useCds = useDiscountService(getState());
        const source = sourceLabel(useCds);
        dispatch(requestDiscountRules(source));

        if (useCds) {
            return fetchFromDiscountService(dispatch, getState)
                .then(rules => dispatch(receiveDiscountRules(rules, source)))
                .catch(error => dispatch(receiveDiscountRulesError(error, source)));
        }

        const user = getState().user;

        return fetchDiscountRulesReq(user.group)
            .then(response => dispatch(receiveDiscountRules(response, source)))
            .catch(error => dispatch(receiveDiscountRulesError(error, source)));
    };
}

/**
 * Read discount rules from cloud-discount-service and map them into the legacy rule shape.
 *
 * Resolution order matters: the merchant supplies the path key for both services, and the
 * translation maps must exist before any cds row can be mapped — so a failure in either step has
 * to surface as a discounts error rather than an empty list, which would render as "no
 * discounts" and hide the problem.
 *
 * @param {function} dispatch
 * @param {function} getState
 * @returns {Promise<Array<object>>} legacy-shaped rules
 */
async function fetchFromDiscountService(dispatch, getState) {
    const merchant = await dispatch(fetchMerchant());
    if (!merchant) {
        throw new Error(getState().merchants.error
            || 'Could not resolve the merchant for this organization');
    }

    const maps = await dispatch(fetchRefMaps());
    if (!maps) {
        throw new Error(getState().refMaps.error
            || 'Could not load product/location reference data');
    }

    const locationTree = await loadLocationTree(dispatch, getState);

    // cds resolves {merchant} against a column holding husky's urlname, not the display name.
    const rows = await listDiscounts(merchant.urlname);
    let rules = cdsListToLegacyRules(rows, { ...maps, locationTree });

    // An unresolvable target id means a rule is rendered narrower than cds actually stored it.
    // That matters because an empty target array renders as "all" in the form, so a
    // site-restricted discount would display as global. Each rule also carries its own
    // `_unresolved` for the in-form notice, and any of them blocks saving.
    //
    // Before reporting that, retry once with rebuilt maps. `fetchRefMaps` caches for the whole
    // session per organization, so a target created *after* the maps were built cannot resolve —
    // and a reload was the only cure. That is easy to hit now that the classic route lets you write
    // on one page and read on the other without a reload. Only the maps can be stale here, so the
    // discount rows are reused rather than re-fetched.
    //
    // Cost is paid only when something is genuinely unresolved: a discount targeting a deleted
    // product will rebuild the maps on every load and still report it. That is the rare case, and
    // it is the one where being sure matters.
    let unresolved = collectUnresolved(rules);
    if (Object.keys(unresolved).length > 0) {
        dispatch(resetRefMaps());
        const rebuilt = await dispatch(fetchRefMaps());
        if (rebuilt) {
            rules = cdsListToLegacyRules(rows, { ...rebuilt, locationTree });
            unresolved = collectUnresolved(rules);
        }
    }
    if (Object.keys(unresolved).length > 0) {
        console.warn('[discounts] cds target ids with no legacy match, after rebuilding the maps:', unresolved);
    }

    return rules;
}

/** Legacy `StatusEnum`: 1 active, 2 deactive, 3 deleted. */
const STATUS_ACTIVE = 1;
const STATUS_DELETED = 3;

/**
 * Dispatch a write's success action, then re-read the list from cds.
 *
 * The reducer merges the returned rule into `discountRules` optimistically, but on the cds path
 * that merge cannot be made correct: a full edit replaces the cds rows, so the rule's `Id` (which
 * for scheduled and bundle *is* the cds row id) changes, and an expiry family's `_raw` is needed
 * to rebuild per-stop names. Synthesizing all of that client-side would mean duplicating
 * `cdsListToLegacyRules` — its expiry grouping, name-suffix stripping and id translation — which
 * is the same "reconstruct instead of read" shortcut that has already produced two bugs here.
 * One GET is cheaper than a second implementation of the mapper.
 *
 * The success action is dispatched first and its result returned unchanged, because the save
 * handlers in `discounts.jsx` test it (`.discountRule` / `.updatedDiscountRule`) to decide whether
 * to show the success toast. The re-read is awaited so the list is correct by the time the form
 * closes; if it fails, `discounts.error` is set and the page shows its error state.
 *
 * @param {function} dispatch
 * @param {object} successAction already-built receive action
 * @returns {Promise<object>} the dispatched success action
 */
async function reReadAfterWrite(dispatch, successAction) {
    const result = dispatch(successAction);
    await dispatch(fetchDiscountRules());
    return result;
}

/**
 * Ask the root API to broadcast a discount refresh to the organisation's cabinets.
 *
 * Needed on the cds route only: writes go to cloud-discount-service, so the root API never sees
 * them and cannot publish the `discounts created/updated` events it publishes for the classic
 * `v2/discounts` route on its own. The write has already succeeded when this runs, so a failed
 * notification is logged rather than surfaced as a save error — the discount is stored either way.
 * @param {Function} getState Redux getState, for the current organisation
 * @param {object} meta Context sent along with the event (`action`, `ruleId`)
 * @returns {Promise<void>}
 */
async function notifyCabinetsOfDiscountChange(getState, meta) {
    const group = getState().user && getState().user.group;
    if (!group || !group.Id) {
        return;
    }
    try {
        await notifyDiscountChangeReq(meta, group);
    } catch (error) {
        console.warn('Discount saved, but cabinets could not be notified to refresh:', error);
    }
}

/** The `meta.action` label for a rule written through the cds path. */
function discountChangeAction(rule, isCreate) {
    if (rule.Status === STATUS_DELETED) return 'deleted';
    return isCreate ? 'created' : 'updated';
}

/**
 * Resolve the merchant and translation maps every write needs.
 *
 * Both are required before a single request goes out: the merchant supplies the URL path key, and
 * an unresolved map would silently drop targets, publishing a discount wider than asked for.
 *
 * **No `currencyCode`.** The obvious-looking source, `products[0].currency`, is wrong twice over:
 * it holds a display *symbol* (`'€'`, set from `currencies[0].symbol` in `products.js:256`), which
 * fails cds's `@Size(min=3,max=3)`; and it is not an org-level value at all — Stora Enso Demo's
 * products carry 12 different symbols, so the first row is arbitrary. Deriving from it would
 * write a confidently wrong currency, which is worse than none.
 *
 * Sending nothing is safe: no legacy discount document carries a currency (zero docs have the
 * field), and the cds calculation core never reads `discount.currency_code` — it is metadata.
 * The authoritative value is `merchant.currency_code` (EUR for every merchant in scope), but
 * `/encoderapp/merchants` does not select it, so exposing it is a core-api change. Until then
 * portal-created rows have a null currency where sync-created rows have EUR.
 * @returns {Promise<{merchant: object, maps: object}>}
 */
async function resolveWriteContext(dispatch, getState) {
    const merchant = await dispatch(fetchMerchant());
    if (!merchant) {
        throw new Error(getState().merchants.error
            || 'Could not resolve the merchant for this organization');
    }
    const maps = await dispatch(fetchRefMaps());
    if (!maps) {
        throw new Error(getState().refMaps.error
            || 'Could not load product/location reference data');
    }
    const locationTree = await loadLocationTree(dispatch, getState);
    return { merchant, maps: { ...maps, locationTree } };
}

/**
 * The org's location hierarchy for the mapper, loading the locations first if this page has not.
 *
 * Both directions need it. Without it a write would target only the chosen locations — and cds
 * would then skip every cabinet in their sub-locations — and a read would list each sub-location
 * as separately chosen. So a failed load is an error, not a silent fallback.
 * @returns {Promise<{parent: object, children: object}>}
 */
async function loadLocationTree(dispatch, getState) {
    if (!getState().cabinets?.locationsFetched) {
        await dispatch(fetchLocations());
    }
    const { cabinets } = getState();
    if (!cabinets?.locationsFetched || cabinets.locationsIsError) {
        throw new Error('Could not load the location hierarchy for this organization');
    }
    // Ancestors of a location-restricted user's own locations cannot be chosen, but they keep
    // the parent links intact.
    return buildLocationTree([...(cabinets.locations || []), ...(cabinets.ancestorLocations || [])]);
}

/**
 * The rule as last read from cds, which is the only place `_cdsIds` and `_reference` live.
 *
 * A full edit rebuilds the rule object from form state, so it arrives without them — only the
 * `Id`. A status toggle spreads the existing rule and keeps them, but reading from the store
 * uniformly means one code path instead of two.
 */
function priorRule(getState, id) {
    const rules = (getState().discounts && getState().discounts.discountRules) || [];
    return rules.find(rule => rule.Id === id) || null;
}

/**
 * True when this save is editing an existing rule rather than creating one.
 *
 * `Id` is the reliable signal, and it comes from the form itself: `ruleId` is
 * `editData ? editData.Id : undefined` (`discounts.jsx:411`), so it is absent on a create and
 * present on every edit, status toggle and delete.
 *
 * Do **not** infer this from whether `priorRule` found a match. That is what the code used to do,
 * and when the lookup missed on an edit two failures compounded silently: no cds ids to delete,
 * *and* a freshly minted expiry `reference` — which is how one discount became two live families.
 */
function isEditingExistingRule(rule) {
    return Boolean(rule.Id);
}

/**
 * Every cds row backing one legacy rule — exactly one per rule since the multi-step expiry
 * migration (an expiry rule's tiers are `expirySteps` on a single row).
 *
 * Returns `[]` only for a create. On an edit the caller must treat an empty result as an error:
 * "replace this rule" with nothing to replace would leave the old rows live alongside the new
 * ones.
 */
function cdsIdsFor(rule, prior) {
    if (prior && Array.isArray(prior._cdsIds) && prior._cdsIds.length) {
        return prior._cdsIds;
    }
    // A scheduled or bundle rule's legacy Id *is* its cds row id (mapper sets `Id: row.id`); an
    // expiry rule's is the reference (the Mongo doc identity), which need not be a row id —
    // hence the guard.
    return rule.Type === 'expiry' || !rule.Id ? [] : [rule.Id];
}

/** Every user group the rule was targeting before this write, so they can be retired after it. */
function userGroupIdsFor(rule, prior) {
    const ids = (prior && prior._userGroupIds) || rule._userGroupIds || [];
    return Array.isArray(ids) ? ids : [];
}

/**
 * Create the LIST user group a restricted rule needs, translating its userKeys to
 * `merchant_user.id`s.
 *
 * Fails rather than writing a partial audience. A userKey with no `merchant_user` row, or one that
 * matched more than one (`ambiguous`), would otherwise silently drop a person from the restriction
 * — and since cds treats "no user group" as "everyone", dropping *all* of them would invert the
 * rule from restricted to public. Same reasoning as the sync's `requireResolved`.
 *
 * @returns {Promise<string>} the new group's id
 */
async function createUserGroupFor(rule, maps, urlname) {
    const userMap = (maps && maps.users) || { toCds: {}, ambiguous: [] };
    const keys = Array.isArray(rule.userIds) ? rule.userIds : [];

    const ambiguous = keys.filter(key => (userMap.ambiguous || []).includes(key));
    if (ambiguous.length) {
        throw new Error('These users match more than one account in the discount service, so the'
            + ` restriction cannot be saved safely: ${ambiguous.join(', ')}`);
    }

    const members = [];
    const missing = [];
    keys.forEach(key => {
        const merchantUserId = userMap.toCds[key];
        if (!merchantUserId) {
            missing.push(key);
            return;
        }
        members.push({ id: merchantUserId });
    });
    if (missing.length) {
        throw new Error('These users are not known to the discount service, so the restriction'
            + ` cannot be saved: ${missing.join(', ')}`);
    }
    if (!members.length) {
        throw new Error('A user-restricted discount needs at least one user.');
    }

    const group = await createUserGroup(urlname, {
        name: rule.Name || 'Discount audience',
        // Traceable back to the discount. Not unique — the sync keys its own group on the same
        // reference — which is safe because the reverse handler resolves membership through the
        // discount's USER_GROUP targets, never by reference.
        reference: rule._reference || rule.Id || undefined,
        users: members,
    });
    if (!group || !group.id) {
        throw new Error('The discount service did not return an id for the new user group.');
    }
    return group.id;
}

/**
 * Delete a group the discount no longer targets. core-api answers 409 (`409012`) while any live
 * discount still targets it; the discount write itself has succeeded by then, so the group is
 * left in place rather than failing the save. Any other error still propagates.
 */
async function retireUserGroup(urlname, userGroupId) {
    try {
        await deleteUserGroup(urlname, userGroupId);
    } catch (error) {
        if (!isStillInUse(error)) {
            throw error;
        }
    }
}

/**
 * Write one legacy rule to cds, choosing between delete, metadata PATCH and full recreate.
 *
 * The portal funnels creation, a full edit, an activate/deactivate toggle and a delete through
 * just two thunks, so this has to discriminate:
 *
 * - `Status: 3` → DELETE every cds row behind the rule.
 * - only patchable fields changed → PATCH. cds accepts `active, name, description, displayName,
 *   displayDescription, displayImageUrl, providerId, reference, translations` and nothing else,
 *   so this covers a pause, a rename and a new image. Taking the recreate path for a mere pause
 *   would mint a new cds id, and with it a new Mongo `_id` for scheduled and bundle rules.
 * - anything else → **create then delete** (mapper rule 8), never delete first. A failed create
 *   leaves the original in place; the reverse order would lose it.
 *
 * Create vs edit comes from `rule.Id`, never from whether the store lookup succeeded — see
 * `isEditingExistingRule`. An edit with no recoverable cds ids (or, for expiry, no recoverable
 * `reference`) is refused rather than degraded, because degrading duplicates the discount instead
 * of replacing it.
 *
 * @param {function} dispatch
 * @param {function} getState
 * @param {object} rule legacy rule from the forms
 * @returns {Promise<object>} the rule, for the existing `receive*` action shape
 */
async function writeToDiscountService(dispatch, getState, rule) {
    const { merchant, maps } = await resolveWriteContext(dispatch, getState);
    const urlname = merchant.urlname;
    const isEdit = isEditingExistingRule(rule);
    const prior = isEdit ? priorRule(getState, rule.Id) : null;
    const existingIds = cdsIdsFor(rule, prior);

    // An edit whose prior rule is not in the store cannot be completed safely: we do not know
    // which cds rows this rule replaces. Proceeding would leave the originals live next to the
    // replacements — and for expiry it would also mint a new `reference`, splitting one discount
    // into two families. A refusal costs a page reload, which re-reads the list from cds and
    // restores `_cdsIds` / `_reference`.
    if (isEdit && !existingIds.length) {
        throw new Error('This discount is out of sync with the discount service.'
            + ' Reload the page and try again.');
    }

    const priorUserGroupIds = userGroupIdsFor(rule, prior);

    if (rule.Status === STATUS_DELETED) {
        await Promise.all(existingIds.map(id => deleteDiscount(urlname, id)));
        // Retire the audience too, so a deleted discount leaves no orphaned group behind.
        await Promise.all(priorUserGroupIds.map(id => retireUserGroup(urlname, id)));
        return { ...rule, _cdsIds: [], _reference: null, _userGroupIds: [] };
    }

    if (prior && existingIds.length && isPatchOnlyChange(rule, prior)) {
        const active = rule.Status === STATUS_ACTIVE;
        await Promise.all(existingIds.map(id => patchDiscountMetadata(urlname, id, {
            active,
            name: rule.Name,
            displayImageUrl: (rule.DiscountData && rule.DiscountData.imageUrl) || null,
        })));
        // The cds rows are unchanged, so carry their identity across — see the note on the
        // recreate path below for why returning a bare rule is not safe. The audience is untouched
        // too: `isPatchOnlyChange` returns false whenever `userIds` or `isUserRestricted` differ.
        return {
            ...rule,
            _cdsIds: existingIds,
            _reference: prior._reference || null,
            _raw: prior._raw,
            _userGroupIds: priorUserGroupIds,
        };
    }

    // An expiry rule's `reference` becomes the Mongo `_id`. Reuse it on edit so the legacy
    // document keeps its identity; mint one **only** for a genuinely new rule — minting during
    // an edit orphans the original document instead of replacing it.
    //
    // There is deliberately no `|| rule._reference` fallback: the form rebuilds the rule from
    // scratch and never carries it, so that branch could never fire and only read as a safety net
    // that was not there. `prior` (re-read from cds after every write) is the single source.
    let reference;
    if (rule.Type === 'expiry') {
        reference = isEdit ? (prior && prior._reference) : uuidv4();
        if (isEdit && !reference) {
            throw new Error('Cannot update this expiry discount: its discount-service reference is'
                + ' unknown. Reload the page and try again.');
        }
    }

    // A restricted rule needs its audience to exist before the discounts can target it. Created
    // first and deliberately not rolled back on a later failure: an unreferenced group is inert,
    // whereas discounts pointing at a group that failed to create would apply to everyone.
    const restricted = Boolean(rule.isUserRestricted)
        || (Array.isArray(rule.userIds) && rule.userIds.length > 0);
    const userGroupId = restricted ? await createUserGroupFor(rule, maps, urlname) : undefined;

    // `backendType` decides which reverse handler the sync routes the row to. Without it cds
    // defaults the column to HUSKY and the Selfly reverse handler never sees the row, so the
    // discount silently never reaches Mongo — and therefore never reaches the cabinet.
    const { payloads, unresolved, unsupported } = legacyRuleToCdsPayloads(rule, maps, {
        reference,
        backendType: merchant.backendType,
        userGroupId,
    });

    if (unsupported.length) {
        throw new Error(`This discount cannot be saved to the discount service yet: ${unsupported.join(', ')}`);
    }
    if (Object.keys(unresolved).length) {
        // Writing anyway would store a *wider* rule than the operator asked for, because an empty
        // target array means "applies everywhere".
        const detail = Object.entries(unresolved).map(([k, ids]) => `${k}: ${ids.join(', ')}`).join('; ');
        throw new Error(`Some targets could not be translated for the discount service (${detail})`);
    }

    const created = [];
    for (const attributes of payloads) {
        created.push(await createDiscount(urlname, attributes));
    }

    // `active` is not settable at create (mapper rule 6), so a rule saved as paused needs this.
    if (rule.Status !== STATUS_ACTIVE) {
        await Promise.all(created
            .filter(row => row && row.id)
            .map(row => patchDiscountMetadata(urlname, row.id, { active: false })));
    }

    // Only now that the replacement exists.
    await Promise.all(existingIds.map(id => deleteDiscount(urlname, id)));

    // The replaced rows were the only thing pointing at the old audience, so retire it — after the
    // delete, for the same reason the delete comes after the create.
    await Promise.all(priorUserGroupIds
        .filter(id => id !== userGroupId)
        .map(id => retireUserGroup(urlname, id)));

    // Carry the new cds identity. `RECEIVE_UPDATE_DISCOUNT_RULE` swaps whatever comes back into
    // `discountRules`, so returning a bare form rule silently strips `_cdsIds` and `_reference` —
    // and nothing refetches on success (the effect in `discounts.jsx` only fires while
    // `!discounts.fetched`). A second edit in the same page session then found no cds ids, deleted
    // nothing, and minted a fresh expiry `reference`, leaving two live families for one discount.
    return {
        ...rule,
        _cdsIds: created.filter(row => row && row.id).map(row => row.id),
        _reference: reference || null,
        _userGroupIds: userGroupId ? [userGroupId] : [],
    };
}

/**
 * Request create discount rule Redux action creator
 */
function requestCreateDiscountRule() {
    return {
        type: REQUEST_CREATE_DISCOUNT_RULE,
    };
}

/**
 * Receive create discount rule Redux action creator
 * @param {object} discountRule
 */
function receiveCreateDiscountRule(discountRule) {
    return {
        type: RECEIVE_CREATE_DISCOUNT_RULE,
        discountRule,
    };
}
function requestUpdateDiscountRule(updatedDiscountRule) {
    return {
        type: REQUEST_UPDATE_DISCOUNT_RULE,
        updatedDiscountRule,
    };
}

function receiveUpdateDiscountRule(updatedDiscountRule) {
    return {
        type: RECEIVE_UPDATE_DISCOUNT_RULE,
        updatedDiscountRule,
    };
}

/**
 * Request create bundle image Redux action creator
 */
export function requestBundleImageUpload(progress) {
    return {
        type: REQUEST_UPLOAD_BUNDLE_IMAGE,
        progress,
    };
}

/**
 * Receive create bundle image success response Redux action creator
 */
export function uploadBundleImageSuccess(url, id) {
    return {
        type: UPLOAD_BUNDLE_IMAGE_SUCCESS,
        url,
        id,
        receivedAt: Date.now(),
    };
}

/**
 * Receive create bundle image error Redux action creator
 * @param {Error} error
 */
function uploadBundleImageError(error) {
    return {
        type: UPLOAD_BUNDLE_IMAGE_ERROR,
        error,
    };
}
/**
 * Request create bundle image Redux action creator
 */
export function requestScheduledImageUpload(progress) {
    return {
        type: REQUEST_UPLOAD_SCHEDULED_IMAGE,
        progress,
    };
}

/**
 * Receive create bundle image success response Redux action creator
 */
export function uploadScheduledImageSuccess(url, id) {
    return {
        type: UPLOAD_SCHEDULED_IMAGE_SUCCESS,
        url,
        id,
        receivedAt: Date.now(),
    };
}

/**
 * Receive create bundle image error Redux action creator
 * @param {Error} error
 */
function uploadScheduledImageError(error) {
    return {
        type: UPLOAD_SCHEDULED_IMAGE_ERROR,
        error,
    };
}
/**
 * Request create bundle image Redux action creator
 */
export function requestExpiryImageUpload(progress) {
    return {
        type: REQUEST_UPLOAD_EXPIRY_IMAGE,
        progress,
    };
}

/**
 * Receive create bundle image success response Redux action creator
 */
export function uploadExpiryImageSuccess(url, id) {
    return {
        type: UPLOAD_EXPIRY_IMAGE_SUCCESS,
        url,
        id,
        receivedAt: Date.now(),
    };
}

/**
 * Receive create bundle image error Redux action creator
 * @param {Error} error
 */
function uploadExpiryImageError(error) {
    return {
        type: UPLOAD_EXPIRY_IMAGE_ERROR,
        error,
    };
}

/**
 * Receive discount rule update error Redux action creator
 * @param {Error} error api request error
 */
function requestUpdateDiscountRuleError(error) {
    return {
        type: RECEIVE_UPDATE_DISCOUNT_RULE_ERROR,
        error,
    };
}

/**
 * Receive discount rule error Redux action creator
 * @param {Error} error api request error
 */
function receiveCreateDiscountRuleError(error) {
    return {
        type: RECEIVE_CREATE_DISCOUNT_RULE_ERROR,
        error,
    };
}

/**
 * Reset bundle image state back to null
 * @param {Error} error api request error
 */
function resetBundleImageState(error) {
    return {
        type: RESET_BUNDLE_IMAGE,
        error,
    };
}
/**
 * Reset expiry image state back to null
 * @param {Error} error api request error
 */
function resetExpiryImageState(error) {
    return {
        type: RESET_EXPIRY_IMAGE,
        error,
    };
}
/**
 * Reset scheduled image state back to null
 * @param {Error} error api request error
 */
function resetScheduledImageState(error) {
    return {
        type: RESET_SCHEDULED_IMAGE,
        error,
    };
}

/**
 * Thunk action for creating a new discount rule
 * @param {object} discountRule
 */
export function createDiscountRule(discountRule) {
    return (dispatch, getState) => {
        dispatch(requestCreateDiscountRule());

        if (useDiscountService(getState())) {
            return writeToDiscountService(dispatch, getState, discountRule)
                .then(async created => {
                    await notifyCabinetsOfDiscountChange(getState, {
                        action: discountChangeAction(created, true), ruleId: created.Id || null,
                    });
                    return reReadAfterWrite(dispatch, receiveCreateDiscountRule(created));
                })
                .catch(error => dispatch(receiveCreateDiscountRuleError(error)));
        }

        const user = getState().user;

        return createDiscountRuleReq(discountRule, user.group)
            .then(createdDiscountRule => dispatch(receiveCreateDiscountRule(createdDiscountRule)))
            .catch(error => dispatch(receiveCreateDiscountRuleError(error)));
    };
}

/**
 * Thunk action for updating a new discount rule
 * @param {object} discountRule
 */
export function updateDiscountRule(discountRule) {
    return (dispatch, getState) => {
        dispatch(requestUpdateDiscountRule());

        if (useDiscountService(getState())) {
            // This thunk carries three different intents — full edit, status toggle and delete.
            // `writeToDiscountService` discriminates them; see its doc comment.
            return writeToDiscountService(dispatch, getState, discountRule)
                .then(async updated => {
                    await notifyCabinetsOfDiscountChange(getState, {
                        action: discountChangeAction(updated, false), ruleId: updated.Id || null,
                    });
                    return reReadAfterWrite(dispatch, receiveUpdateDiscountRule(updated));
                })
                .catch(error => dispatch(requestUpdateDiscountRuleError(error)));
        }

        const user = getState().user;

        return updateDiscountRuleReq(discountRule, user.group)
            .then(updatedDiscountRule => dispatch(receiveUpdateDiscountRule(updatedDiscountRule)))
            .catch(error => dispatch(requestUpdateDiscountRuleError(error)));
    };
}

/**
 *
 * @param {File} imageFile
 */
export function uploadBundleImage(imageFile) {
    return (dispatch, getState) => {
        // update state to inform API call started
        dispatch(requestBundleImageUpload());
        // convert image file to HTTP form data with unique filename
        const formData = new FormData();
        const fileName = `${uuidv1()}.jpeg`;
        //`${uuidv1()}.jpeg`
        formData.append(0, imageFile, fileName);

        // store image in backend
        const user = getState().user;

        return uploadDiscountImageHttpRequest(formData, user.group, 'bundle')
            .then(response => dispatch(uploadBundleImageSuccess(encodeURI(response.url), response.id)))
            .catch(error => dispatch(uploadBundleImageError(error)));
    };
}

export function resetBundleImage() {
    return dispatch => {
        // update state to inform API call started
        dispatch(resetBundleImageState());
    };
}

/**
 *
 * @param {File} imageFile
 */
export function uploadExpiryImage(imageFile) {
    return (dispatch, getState) => {
        // update state to inform API call started
        dispatch(requestExpiryImageUpload());
        // convert image file to HTTP form data with unique filename
        const formData = new FormData();
        const fileName = `${uuidv1()}.jpeg`;
        //`${uuidv1()}.jpeg`
        formData.append(0, imageFile, fileName);

        // store image in backend
        const user = getState().user;

        return uploadDiscountImageHttpRequest(formData, user.group, 'expiry')
            .then(response => dispatch(uploadExpiryImageSuccess(encodeURI(response.url), response.id)))
            .catch(error => dispatch(uploadExpiryImageError(error)));
    };
}

export function resetExpiryImage() {
    return dispatch => {
        // update state to inform API call started
        dispatch(resetExpiryImageState());
    };
}

/**
 *
 * @param {File} imageFile
 */
export function uploadScheduledImage(imageFile) {
    return (dispatch, getState) => {
        // update state to inform API call started
        dispatch(requestScheduledImageUpload());
        // convert image file to HTTP form data with unique filename
        const formData = new FormData();
        const fileName = `${uuidv1()}.jpeg`;
        //`${uuidv1()}.jpeg`
        formData.append(0, imageFile, fileName);

        // store image in backend
        const user = getState().user;

        return uploadDiscountImageHttpRequest(formData, user.group, 'scheduled')
            .then(response => dispatch(uploadScheduledImageSuccess(encodeURI(response.url), response.id)))
            .catch(error => dispatch(uploadScheduledImageError(error)));
    };
}

export function resetScheduledImage() {
    return dispatch => {
        // update state to inform API call started
        dispatch(resetScheduledImageState());
    };
}

/**
 * Discard the loaded discount rules on organisation change.
 *
 * **Reset only — deliberately does not refetch.** It used to, but with two discount routes an
 * outside-the-page fetch has to guess which backend to use, and on the classic route it would
 * guess wrong. RESET clears `fetched` and `source`, which the mounted page's effect sees as stale
 * and reloads using its own route's backend. `state.discounts` has exactly one consumer
 * (`discounts.jsx`), so nothing else depends on the eager fetch — and when no discounts page is
 * mounted this now avoids a request whose result was thrown away.
 */
export function refreshDiscounts() {
    return dispatch => dispatch({ type: RESET_DISCOUNT_RULES });
}
